/**
 * Maps a backend `/api/tokens` row to the shape legacy premium UI components
 * expect. The backend is the source of truth — every number here comes from
 * the indexer, nothing is computed client-side.
 *
 * Legacy shape (PremiumCard / DexTable / FeaturedRow / TrendingSection):
 *   { contractAddress, chain, name, symbol, image: { url },
 *     market_cap_usd, volume_usd_24h, price_change_24h,
 *     holders, age_seconds, website, twitter, telegram, sparkline }
 */
/**
 * Sparkline columns come back as pg numeric[] → JS array of stringified
 * numbers. Coerce to plain Number[] so the SVG can do math on it directly.
 * Falls back to empty array — Sparkline component no-ops on <2 points.
 */
const adaptSparkline = (arr) => {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const v of arr) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out;
};

export function adaptSlurpToken(row) {
  if (!row || !row.mint) return null;
  const createdMs = row.created_at ? new Date(row.created_at).getTime() : Date.now();
  const ageSeconds = Math.max(0, Math.floor((Date.now() - createdMs) / 1000));
  return {
    contractAddress: row.mint,
    mint: row.mint,
    pool: row.pool,
    chain: "Solana",
    name: row.name || row.symbol || (row.mint ? row.mint.slice(0, 6) : ""),
    symbol: row.symbol || "",
    image: row.image_url ? { url: row.image_url } : null,
    description: row.description || "",
    market_cap_usd: Number(row.market_cap_usd || 0),
    volume_usd_24h: Number(row.volume_usd_24h || 0),
    last_price_usd: Number(row.last_price_usd || 0),
    price_change_24h: Number(row.price_change_24h || 0),
    holders: Number(row.holder_count || row.holders_count || 0),
    age_seconds: ageSeconds,
    website: row.website || "",
    twitter: row.twitter || "",
    telegram: row.telegram || "",
    created_at: row.created_at,
    // Used by the dashboard's "Recent Trades" (Bumps) sort. Prefer
    // `last_traded_at` — the dedicated backend column that is ONLY
    // bumped on real trade inserts. Old fallback to `updated_at` was
    // broken because decay sweep, dex poller, autoburn, lpCompound,
    // and holder reconcile all bump `updated_at`, which flattened the
    // Bumps order to worker activity instead of trade recency.
    last_trade_at: row.last_traded_at || row.last_trade_at || row.created_at,
    sparkline: adaptSparkline(row.sparkline),
    // Per-token fee handling. "burn" = creator pledged buy-back-and-burn,
    // "lp_compound" = fees auto-compound back into the pool, "claim" =
    // creator claims SOL directly. UI surfaces a different badge per mode.
    fee_mode:
      row.fee_mode === "burn" ? "burn" :
      row.fee_mode === "lp_compound" ? "lp_compound" :
      "claim",
    // Cumulative raw amount burned via buy-back-and-burn. Display code
    // divides by 10**decimals for the "X tokens burnt" label.
    total_burned_raw: row.total_burned_raw ? String(row.total_burned_raw) : "0",
    // Running USD value of fees the LP-compound worker has fed back into
    // the pool. Display code formats as "$X added to LP".
    lp_added_usd: row.lp_added_usd != null ? String(row.lp_added_usd) : "0",
    decimals: Number(row.decimals ?? 6),
    // DexScreener paid-profile signal — set by the backend dex poller
    // once the creator has uploaded a paid header image.
    dex_paid_at: row.dex_paid_at ?? null,
    _live: true,
  };
}

export function adaptSlurpTokens(rows = []) {
  return rows.map(adaptSlurpToken).filter(Boolean);
}
